import { closeSharingDialog } from '../actions/internalActions';
import { ItemWithPolicyTip } from '../types/ItemWithPolicyTip';
import { SharingDialogResponse } from '../types/SharingDialogResponse';
import { IPeoplePickerProvider } from '@ms/odsp-datasources/lib/PeoplePicker';
import { ISharingProvider } from '@ms/odsp-datasources/lib/Sharing';
import { Share as ShareComponent } from '@ms/odsp-shared-sharing/lib/components/Share/index';
import SharingStore from '@ms/odsp-shared-sharing/lib/store/odb/SharingStore';
import PlatformDetection from '@ms/odsp-utilities/lib/browser/PlatformDetection';
import ResponsiveUI from '@ms/odsp-utilities/lib/browser/ResponsiveUI';
import { IManager } from '@ms/telemetry-manager/lib/Manager';
import { logUsage, PerformanceDatapoint } from 'owa-analytics';
import { getAttachmentPermissionLevelFromSharingLinkKindAndAudience } from 'owa-attachment-permission';
import { OwaSharingAudience } from 'owa-attachment-permission';
import { asOwaDateInUserTz } from 'owa-datetime';
import AttachmentPermissionLevel from 'owa-service/lib/contract/AttachmentPermissionLevel';
import SharingLinkKind from 'owa-service/lib/contract/SharingLinkKind';
import * as PropTypes from 'prop-types';
import * as React from 'react';
import {
    ISharingLinkV3 as ISharingLink,
    SharingAudience,
} from '@ms/odsp-datasources/lib/interfaces/sharing/SharingInterfaces';
import {
    ClientId,
    ICustomLabeledAudienceConfig,
    ISharingContextInformation,
    ISharingInformation,
    ISharingItemInformation,
    Mode,
} from '@ms/odsp-shared-sharing/lib/interfaces/SharingInterfaces';

export interface ShareProps extends React.Props<any> {
    /* Indicates the existing share link id used in the LinkSettings mode */
    shareId?: string;

    /* Allows adding a custom labeled audience  option in the link settings view. ICustomLabelAudienceConfig object */
    customLabeledAudienceConfig?: ICustomLabeledAudienceConfig;

    /* The onDismiss callback executed when the share component closes. */
    onDismiss?: (response?: SharingDialogResponse) => void;

    /* PeoplePickerProvider instance to make people picker API calls. */
    peoplePickerProvider?: IPeoplePickerProvider;

    /* The parsed response from GetSharingInformation API, provided by the caller. */
    sharingInformation?: ISharingInformation;

    /* SharingProvider instance to make sharing API calls in SharingStore. */
    sharingProvider?: ISharingProvider<ItemWithPolicyTip>;

    permissionLevel?: AttachmentPermissionLevel;

    /* used to log data for OneDrive */
    telemetryManager: IManager;
}

export default class SharingDialog extends React.Component<ShareProps, {}> {
    private onDismiss: () => void;
    private sharingStore: SharingStore<ItemWithPolicyTip>;
    private sharingItemInformation: ISharingItemInformation;
    private peoplePickerProvider: IPeoplePickerProvider;
    private maxHeight: number;
    private platformDetection: PlatformDetection;
    private breakpoint: number;
    private isLoaded: boolean;
    private hasLoggedError: boolean;
    private datapoint: PerformanceDatapoint;

    public static childContextTypes = {
        peoplePickerProvider: PropTypes.object.isRequired,
        resize: PropTypes.func.isRequired,
        onDismiss: PropTypes.func,
        sharingStore: PropTypes.object.isRequired,
        usesSharingAbilities: PropTypes.bool.isRequired,
        platformDetection: PropTypes.object.isRequired,
        breakpoint: PropTypes.object.isRequired,
        isMacV4ExperimentOn: PropTypes.bool.isRequired,
    };

    constructor(props: ShareProps) {
        super(props);
        this.datapoint = new PerformanceDatapoint('sharingDialogOpened');
        const sharingContextInfoString = 'sharingContextInfo';
        const sharingContextInfo = window[sharingContextInfoString] as ISharingContextInformation;
        this.onDismiss = props.onDismiss;
        this.peoplePickerProvider = props.peoplePickerProvider;
        this.sharingStore = new SharingStore(
            {
                item: null,
                sharingInformation: props.sharingInformation,
                defaultRecipients: null,
                accessChecked: false,
                mode: Mode.linkSettings,
                sharingContextInformation: sharingContextInfo,
                telemetryManager: props.telemetryManager,
            },
            {
                sharingProvider: props.sharingProvider,
                suiteNavProvider: null,
                peoplePickerProvider: props.peoplePickerProvider,
                policyTipProvider: null,
                graphPeoplePickerHelper: null,
            }
        );
        this.platformDetection = new PlatformDetection();
        this.breakpoint =
            ResponsiveUI.DEFAULT_BREAKPOINTS[ResponsiveUI.determineWindowSize(window.innerWidth)];

        this.maxHeight = 550;
        this.isLoaded = false;
        this.hasLoggedError = false;

        let totalItemCount = 0;
        let itemName = ' ';

        if (sharingContextInfo) {
            itemName = sharingContextInfo.itemName || ' ';
            totalItemCount = sharingContextInfo.itemCount || 0;
        }

        this.sharingItemInformation = {
            name: itemName,
            childCount: totalItemCount,
            hasDlpPolicy: undefined,
        };
    }

    // getChildContext is used by <ShareComponent /> to get its context. It is a part of the pattern used by OneDrive, and
    // in order to use ShareComponent we are required to have this, with these exact properties.
    public getChildContext() {
        return {
            peoplePickerProvider: this.peoplePickerProvider,
            resize: () => null,
            onDismiss: this.onDismiss,
            sharingStore: this.sharingStore,
            usesSharingAbilities: true,
            platformDetection: this.platformDetection,
            breakpoint: this.breakpoint,
            isMacV4ExperimentOn: false,
        };
    }

    public render(): JSX.Element {
        const props = this.props;
        const preferCustomLabeledAudience: boolean =
            props.permissionLevel === AttachmentPermissionLevel.Edit ||
            props.permissionLevel === AttachmentPermissionLevel.View;

        return (
            <div>
                <ShareComponent
                    shareId={props.shareId}
                    clientId={ClientId.outlookWeb}
                    scenarioId={''}
                    customLabeledAudienceConfig={props.customLabeledAudienceConfig}
                    showExistingAccessOption={true}
                    hideShareDialogOverflowButton={false}
                    enableShimmer={false}
                    createPeopleSharingLinks={true}
                    maxHeight={this.maxHeight}
                    mode={Mode.linkSettings}
                    messageText={''}
                    sharingItemInformation={this.sharingItemInformation}
                    preferCustomLabeledAudience={preferCustomLabeledAudience}
                />
            </div>
        );
    }

    componentWillUnmount() {
        this.endDatapoint('componentUnmounted');
        this.sharingStore.dispose();
        window.removeEventListener('message', this.onPostMessageReceived, false);
    }

    // Note: With Satchel/mobx we should not be doing this. The correct pattern is to store the sharingLink
    // in sharingDialogStore, and access the store with a selector, outside of this component. We would have a utility
    // That would listen to the post messages, that would fire off an action, and update the store.
    // However, <ShareComponent /> is an external component that is not using Satchel, and is setting the sharingLink.
    // Since <ShareComponent /> is not using Satchel, it does not have actions, so it cannot modify our store.
    // We do all of the logic in the component to work around this issue
    componentDidMount() {
        window.addEventListener('message', this.onPostMessageReceived, false);
    }

    private onPostMessageReceived = (event: any) => {
        if (!event.data) {
            return;
        }

        const messageData = JSON.parse(event.data);
        if (!messageData.name) {
            return;
        }

        switch (messageData.name) {
            case 'share_resize':
                break;
            case 'share_dismiss':
            case 'share_completed':
                closeSharingDialog();
                break;
            case 'share_linkSettingsCompleted':
                this.createSharingDialogResponse();
                break;
            case 'share_error':
                // Currently the sharing dialog sends a post message with the error info twice
                // Once when the error happens, and once when the dialog is being dismissed
                // We are using a 'has logged error' check to filter duplicate data, while not losing data
                // if this is fixed.
                logUsage(
                    'SharingDialogError',
                    {
                        isLoaded: this.isLoaded,
                        hasLoggedError: this.hasLoggedError,
                    },
                    {
                        cosmosOnlyData: JSON.stringify({
                            error: messageData.error,
                            errorCategory: messageData.errorCategory,
                            errorMessage: messageData.errorMessage,
                            debugErrorMessage: messageData.debugErrorMessage,
                        }),
                    }
                );
                this.hasLoggedError = true;

                // We do not get a 'share ready' if the dialog errors before it is ready.
                this.endDatapoint('sharingDialogErrored');
                break;
            case 'share_ready':
                this.isLoaded = true;
                this.endDatapoint('sharingDialogLoaded');
                break;
            default:
                break;
        }
    };

    private createSharingDialogResponse() {
        const sharingLink: ISharingLink = this.sharingStore.getSharingLinkCreated();
        if (sharingLink) {
            const linkKind = sharingLink.sharingLinkKind as number;
            const owaAudience = this.convertToOwaSharingAudience(sharingLink.audience);

            const permissionLevel: AttachmentPermissionLevel = getAttachmentPermissionLevelFromSharingLinkKindAndAudience(
                linkKind,
                owaAudience,
                sharingLink.isEdit
            );

            const sharingDialogResponse: SharingDialogResponse = {
                shareId: sharingLink.shareId,
                url: sharingLink.url,
                linkKind: linkKind as SharingLinkKind,
                permissionLevel: permissionLevel,
                isEdit: sharingLink.isEdit,
                expirationDate: asOwaDateInUserTz(sharingLink.expiration) || null,
                blocksDownload: sharingLink.blockDownload,
            };

            this.props.onDismiss(sharingDialogResponse);
        }
    }

    private convertToOwaSharingAudience(audience: SharingAudience): OwaSharingAudience {
        switch (audience) {
            case SharingAudience.specificPeople:
                return OwaSharingAudience.specificPeople;
            case SharingAudience.organization:
                return OwaSharingAudience.organization;
            case SharingAudience.anyone:
                return OwaSharingAudience.anyone;
            case SharingAudience.existing:
                return OwaSharingAudience.existing;
            case SharingAudience.specificPeopleFlex:
                return OwaSharingAudience.specificPeopleFlex;
            case SharingAudience.customLabeledSpecificPeople:
                return OwaSharingAudience.customLabeledSpecificPeople;
            default:
                return OwaSharingAudience.existing;
        }
    }

    private endDatapoint(reason: string) {
        if (this.datapoint) {
            this.datapoint.addCustomData({ datapointEndReason: reason });
            this.datapoint.end();
            this.datapoint = null;
        }
    }
}
